import Activity from '@client-shared/models/activity.model.js'
import Task from '@client-shared/models/task.model.js'
import { getResponseData } from '@client-shared/utils/get-response-data.js'

export const getTasks = async ({
  axios,
  projectId,
  loadAll,
  signal,
}) => {
  const response = await axios.get('tasks', {
    signal,
    params: {
      projectid: projectId,
      loadAll: loadAll || undefined,
    },
  })

  const data = getResponseData(response)

  if (!data) { return }

  // Freeze Objects to prevent data binding on tasks which causes slow performance
  const tasks = data.list.map(p => Object.freeze(new Task(p)))

  return {
    tasks,
    missingPublishedTasks: data.missingPublishedTasks,
    missingTrashedTasks: data.missingTrashedTasks,
  }
}

export const getTask = async ({
  axios,
  taskId,
  signal,
}) => {
  const response = await axios.get(`tasks/${taskId}`, { signal })

  const data = getResponseData(response)

  if (!data) { return }

  return new Task(data)
}

export const postTask = async ({
  axios,
  task,
  deduplicationId,
}) => {
  const { data } = await axios.post('tasks/', task, {
    params: {
      deduplicationId,
    },
  })

  return new Task(data)
}

export const exportTasks = async ({
  axios,
  itemIds,
  format,
  projectId,
  filter,
}) => {
  const { data } = await axios.post('tasks/export',
    {
      itemIds,
      format,
      projectId,
      filter,
    },
    {
      responseType: 'blob',
    })

  return data
}

export const getLatestCreatedTaskByUser = async ({
  axios,
  projectId,
}) => {
  const { data } = await axios.get('tasks/latest-by-user', {
    params: {
      projectid: projectId,
    },
  })

  return data && Object.keys(data).length > 0 ? new Task(data) : undefined // keys check needed for backup app
}

export const updateTaskType = async ({
  axios,
  task,
  type,
}) => {
  const patchObject = {
    op: 'update',
    path: 'type',
    value: {
      old: task.type,
      new: type,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskAssigneeUserId = async ({
  axios,
  task,
  userId,
}) => {
  const patchObject = {
    op: 'update',
    path: 'assigneeUserId',
    value: {
      old: task.assigneeUserId,
      new: userId,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskDueDate = async ({
  axios,
  task,
  dueDate,
}) => {
  const patchObject = {
    op: 'update',
    path: 'dueDate',
    value: {
      old: task.dueDate,
      new: dueDate,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskLocations = async ({
  axios,
  task,
  location1,
  location2,
  location3,
  location4,
  locationLayerId,
  locationLayerVersionId,
  locationPins,
}) => {
  const patchObject = {
    op: 'update',
    path: 'locations',
    value: {
      old: {
        location1: task.location1,
        location2: task.location2,
        location3: task.location3,
        location4: task.location4,
        locationLayerId: task.locationLayerId,
        locationLayerVersionId: task.locationLayerVersionId,
        locationPins: task.locationPins,
      },
      new: {
        location1,
        location2,
        location3,
        location4,
        locationLayerId,
        locationLayerVersionId,
        locationPins,
      },
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskLocation = async ({
  axios,
  task,
  locationProperty,
  location,
}) => {
  const patchObject = {
    op: 'update',
    path: locationProperty,
    value: {
      old: task[locationProperty],
      new: location,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskTags = async ({
  axios,
  task,
  tags,
}) => {
  const patchObject = {
    op: 'update',
    path: 'tags',
    value: {
      old: task.tags,
      new: tags,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const publishTask = async ({
  axios,
  task,
}) => {
  const patchObject = {
    op: 'execute',
    path: 'publish',
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskTitle = async ({
  axios,
  task,
  title,
}) => {
  const patchObject = {
    op: 'update',
    path: 'title',
    value: {
      old: task.title,
      new: title,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const updateTaskDescription = async ({
  axios,
  task,
  description,
}) => {
  const patchObject = {
    op: 'update',
    path: 'description',
    value: {
      old: task.description,
      new: description,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const addTaskPfFileIds = async ({
  axios,
  taskId,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'add',
    path: 'pfFileIds',
    value: pfFileIds,
  }

  const { data } = await axios.patch(`tasks/${taskId}`, patchObject)

  return new Task(data)
}

export const updateTaskPfFileIds = async ({
  axios,
  taskId,
  oldPfFileIds,
  newPfFileIds,
}) => {
  const patchObject = {
    op: 'update',
    path: 'pfFileIds',
    value: {
      old: oldPfFileIds,
      new: newPfFileIds,
    },
  }

  const { data } = await axios.patch(`tasks/${taskId}`, patchObject)

  return new Task(data)
}

export const updateTaskPfFileId = async ({
  axios,
  taskId,
  oldPfFileId,
  newPfFileId,
}) => {
  const patchObject = {
    op: 'update',
    path: 'pfFileId',
    value: {
      old: oldPfFileId,
      new: newPfFileId,
    },
  }

  const { data } = await axios.patch(`tasks/${taskId}`, patchObject)

  return new Task(data)
}

export const updateTaskCommentPfFileId = async ({
  axios,
  taskId,
  taskCommentId,
  oldPfFileId,
  newPfFileId,
}) => {
  const patchObject = {
    op: 'update',
    path: 'pfFileId',
    value: {
      old: oldPfFileId,
      new: newPfFileId,
    },
  }

  const { data } = await axios.patch(`tasks/${taskId}/comments/${taskCommentId}`, patchObject)

  return new Task(data)
}

export const deleteTaskPfFileIds = async ({
  axios,
  taskId,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'delete',
    path: 'pfFileIds',
    value: pfFileIds,
  }

  const { data } = await axios.patch(`tasks/${taskId}`, patchObject)

  return new Task(data)
}

export const reorderTaskPfFileIds = async ({
  axios,
  task,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'reorder',
    path: 'pfFileIds',
    value: {
      old: task.pfFileIds,
      new: pfFileIds,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const reorderTaskCommentPfFileIds = async ({
  axios,
  task,
  taskComment,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'reorder',
    path: 'pfFileIds',
    value: {
      old: taskComment.pfFileIds,
      new: pfFileIds,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}/comments/${taskComment._id}`, patchObject)

  return new Task(data)
}

export const postTaskComment = async ({
  axios,
  taskId,
  taskComment,
}) => {
  const { data } = await axios.post(`tasks/${taskId}/comments`, taskComment)

  return new Task(data)
}

export const updateTaskCommentPfFileIds = async ({
  axios,
  taskId,
  taskCommentId,
  oldPfFileIds,
  newPfFileIds,
}) => {
  const patchObject = {
    op: 'update',
    path: 'pfFileIds',
    value: {
      old: oldPfFileIds,
      new: newPfFileIds,
    },
  }

  const { data } = await axios.patch(`tasks/${taskId}/comments/${taskCommentId}`, patchObject)

  return new Task(data)
}

export const addTaskCommentPfFileIds = async ({
  axios,
  taskId,
  taskCommentId,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'add',
    path: 'pfFileIds',
    value: pfFileIds,
  }

  const { data } = await axios.patch(`tasks/${taskId}/comments/${taskCommentId}`, patchObject)

  return new Task(data)
}

export const deleteTaskCommentPfFileIds = async ({
  axios,
  taskId,
  taskCommentId,
  pfFileIds,
}) => {
  const patchObject = {
    op: 'delete',
    path: 'pfFileIds',
    value: pfFileIds,
  }

  const { data } = await axios.patch(`tasks/${taskId}/comments/${taskCommentId}`, patchObject)

  return new Task(data)
}

export const updateTaskCommentMessage = async ({
  axios,
  task,
  taskComment,
  message,
}) => {
  const patchObject = {
    op: 'update',
    path: 'message',
    value: {
      old: taskComment.message,
      new: message,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}/comments/${taskComment._id}`, patchObject)

  return new Task(data)
}

export const updateTrashed = async ({
  axios,
  task,
  isTrashed,
}) => {
  const patchObject = {
    op: 'update',
    path: 'trashed',
    value: {
      old: task.trashed,
      new: isTrashed,
    },
  }

  const { data } = await axios.patch(`tasks/${task._id}`, patchObject)

  return new Task(data)
}

export const deleteTaskHard = async ({
  axios,
  task,
}) => {
  await axios.delete(`tasks/${task._id}`)
}

export const getSignedTaskLocationMapImageDownloadUrl = async ({
  axios,
  taskId,
  contentDisposition = 'inline',
  resolution = '',
}) => {
  if (![
    'attachment',
    'inline',
  ].includes(contentDisposition)) {
    throw new Error('Invalid contentDisposition')
  }

  const { data } = await axios.get(`tasks/${taskId}/location-map-image?contentDisposition=${contentDisposition}&resolution=${resolution}`)

  return data.url
}

export const getActivitiesFromTask = async ({
  axios,
  taskId,
}) => {
  const response = await axios.get(`tasks/${taskId}/activities`)

  const data = getResponseData(response)

  if (!Array.isArray(data)) { return [] }

  return data.map(p => new Activity(p))
}

export const getActivitiesFromTaskComment = async ({
  axios,
  taskId,
  commentId,
}) => {
  const response = await axios.get(`tasks/${taskId}/comments/${commentId}/activities`)

  const data = getResponseData(response)

  if (!Array.isArray(data)) { return [] }

  return data.map(p => new Activity(p))
}

// send and preview post the identical body ({ taskIds, receiverEmails,
// mailData, subject }) so the previewed payload can never drift from the sent one
export const triggerTaskReminderSend = async ({ axios, ...reminderArgs }) => {
  const { data } = await axios.post('tasks/mail/', reminderArgs)

  return new Activity(data)
}
